import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import {
  USER_CAN_DEPOSIT,
  DELEGATE_MY_TASKS,
  TIP_CUE_WORKFLOW,
} from 'src/app/services/constants';

@Component({
  selector: 'app-permission-selector',
  templateUrl: './permission-selector.component.html',
  styleUrls: ['./permission-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PermissionSelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PermissionSelectorComponent),
      multi: true,
    },
  ],
})
export class PermissionSelectorComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() permissions: any[] = [];
  @Input() accessPermissions: any[] = [];
  @Input() userFeatures: {
    allocated?: boolean;
    pointsEligible?: boolean;
    sub_budget_allocation?: boolean;
  } = {};

  permissionForm!: FormGroup;
  isLevelNineSelected: boolean = false;
  userCanDeposit = USER_CAN_DEPOSIT;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidationChange: () => void = () => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();
    this.setupValueChanges();
  }

  private initializeForm(): void {
    this.permissionForm = this.fb.group({
      userRoles: [[]],
      userControls: [[]],
    });
  }

  private setupValueChanges(): void {
    this.permissionForm.valueChanges.subscribe(() => {
      this.emitValue();
      this.onValidationChange();
    });
  }

  setPermissions(selectedOptions: any[]) {
    const fifthLevel = 5;
    const sixthLevel = 6;
    const ninthLevel = 9;

    const fifth = this.permissions.find((p) => p.level === fifthLevel);
    const sixth = this.permissions.find((p) => p.level === sixthLevel);
    const ninth = this.permissions.find((p) => p.level === ninthLevel);

    if (!fifth || !sixth || !ninth) return;

    const hasSixth = selectedOptions.some((opt) => opt.level === sixthLevel);
    const hasFifth = selectedOptions.some((opt) => opt.level === fifthLevel);
    const hasNinth = selectedOptions.some((opt) => opt.level === ninthLevel);

    let updatedSelection = [...selectedOptions];

    // Rule 1: If level 6 is selected, auto-select level 5
    if (hasSixth && !hasFifth) {
      updatedSelection.push(fifth);
    }

    // Rule 2: If level 9 (Tip-and-Cue) is selected, select ALL permissions
    if (hasNinth) {
      updatedSelection = [...this.permissions];
    }

    this.isLevelNineSelected = hasNinth;
    this.permissionForm.get('userRoles')?.setValue(updatedSelection, { emitEvent: false });
    this.emitValue();
  }

  close(mySelect: MatSelect, event: Event): void {
    event.stopPropagation();
    mySelect.close();
  }

  isFifthBlocked(option: any): boolean {
    const selected = this.permissionForm.get('userRoles')?.value || [];
    const hasSixth = selected.some((opt) => opt.level === 6);
    return option.level === 5 && hasSixth;
  }

  get f() {
    return this.permissionForm.controls;
  }

  private emitValue(): void {
    const userRoles = this.permissionForm.get('userRoles')?.value || [];
    const userControls = this.permissionForm.get('userControls')?.value || [];
    
    // Combine both arrays and emit
    const combinedPermissions = [...userRoles, ...userControls];
    
    this.onChange(combinedPermissions);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value && Array.isArray(value)) {
      // Split incoming permissions into userRoles and userControls
      const accessLevels = [DELEGATE_MY_TASKS, USER_CAN_DEPOSIT];
      const userRoles = value.filter((p) => !accessLevels.includes(p.level));
      const userControls = value.filter((p) => accessLevels.includes(p.level));

      this.permissionForm.patchValue(
        {
          userRoles: userRoles,
          userControls: userControls,
        },
        { emitEvent: false }
      );
      
      // Check if level 9 is selected
      this.isLevelNineSelected = userRoles.some((p) => p.level === TIP_CUE_WORKFLOW);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.permissionForm.disable();
    } else {
      this.permissionForm.enable();
    }
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.permissionForm) {
      return null;
    }

    const userRolesControl = this.permissionForm.get('userRoles');
    const errors: ValidationErrors = {};

    // Check if userRoles is required and empty
    if (userRolesControl?.hasError('required')) {
      errors['required'] = true;
    }

    // Check if userRoles has value but is empty array
    const userRolesValue = userRolesControl?.value || [];
    if (Array.isArray(userRolesValue) && userRolesValue.length === 0) {
      errors['required'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }
}
